import React, { useState, useEffect, useCallback } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { GitCompare, Calendar as CalendarIcon, BarChart3, Loader2, AlertTriangle, CheckCircle2 } from "lucide-react";

const fmt = (n) => {
  if (n === null || n === undefined) return "0";
  const v = Math.round(n);
  return new Intl.NumberFormat("vi-VN").format(v);
};

const fmtDiff = (v) => {
  if (!v || v === 0) return "0";
  const s = new Intl.NumberFormat("vi-VN").format(Math.abs(v));
  return v > 0 ? `+${s}` : `-${s}`;
};

const formatDateVN = (d) => {
  if (!d || d.length < 10) return d;
  return `${d.slice(8, 10)}/${d.slice(5, 7)}/${d.slice(0, 4)}`;
};

const diffColor = (v) => {
  if (!v || v === 0) return "text-emerald-400";
  return Math.abs(v) > 100000 ? "text-red-400" : "text-amber-400";
};

const FILTER_PRESETS = [
  { key: "1d", label: "Hôm nay" },
  { key: "3d", label: "3 ngày" },
  { key: "7d", label: "7 ngày" },
  { key: "month", label: "Tháng này" },
  { key: "custom", label: "Tuỳ chọn" },
];

export default function ReconcileKVTab() {
  const today = new Date().toISOString().slice(0, 10);
  const [filterPreset, setFilterPreset] = useState("7d");
  const [customStart, setCustomStart] = useState(today);
  const [customEnd, setCustomEnd] = useState(today);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterPreset === "custom") {
        params.set("filter", "custom");
        params.set("start", customStart);
        params.set("end", customEnd);
      } else {
        params.set("filter", filterPreset);
      }
      const { data: d } = await api.get(`/accounting/reconcile-kv?${params.toString()}`);
      setData(d);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Lỗi tải đối chiếu");
    } finally {
      setLoading(false);
    }
  }, [filterPreset, customStart, customEnd]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const kt = data?.ke_toan;
  const kv = data?.kiotviet;
  const diff = data?.diff;
  const pippy = data?.ke_toan_pippy;

  return (
    <div className="space-y-3" data-testid="reconcile-kv-tab">
      {/* Banner */}
      <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg px-3 py-2 text-[10.5px] text-amber-300">
        ⓘ Tab này chỉ <strong>READ-ONLY so sánh</strong> giữa Kế toán (Doanh thu ngày + Pippy) và KiotViet API.
        Hệ thống KHÔNG sửa data của 2 nguồn — chỉ tính diff để bạn xem tham khảo và đối chiếu.
      </div>

      {/* Header + Filter */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h2 className="text-sm font-bold text-slate-200 flex items-center gap-2">
          <GitCompare className="w-4 h-4 text-amber-400" /> Đối chiếu KiotViet
        </h2>
        <div className="flex items-center gap-2 flex-wrap">
          {FILTER_PRESETS.map((p) => (
            <button
              key={p.key}
              onClick={() => setFilterPreset(p.key)}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                filterPreset === p.key
                  ? "bg-amber-500 text-white"
                  : "bg-[#1e293b] text-slate-400 hover:text-slate-200 hover:bg-[#334155]"
              }`}
              data-testid={`reconcile-filter-${p.key}`}
            >
              {p.label}
            </button>
          ))}
          {filterPreset === "custom" && (
            <>
              <Input
                type="date"
                value={customStart}
                onChange={(e) => setCustomStart(e.target.value)}
                className="h-8 bg-[#0f172a] border-[#334155] text-slate-200 text-xs w-[130px]"
                data-testid="reconcile-custom-start"
              />
              <span className="text-slate-500 text-xs">→</span>
              <Input
                type="date"
                value={customEnd}
                onChange={(e) => setCustomEnd(e.target.value)}
                className="h-8 bg-[#0f172a] border-[#334155] text-slate-200 text-xs w-[130px]"
                data-testid="reconcile-custom-end"
              />
            </>
          )}
        </div>
      </div>

      {loading ? (
        <Loader2 className="w-5 h-5 text-amber-400 animate-spin mx-auto mt-8" />
      ) : !data ? (
        <p className="text-center text-slate-500 text-sm py-12">Chưa có dữ liệu</p>
      ) : (
        <>
          {/* Cards 3 nguồn */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="reconcile-summary-cards">
            <div className="bg-gradient-to-br from-blue-600/15 to-blue-500/5 border border-blue-500/30 rounded-xl p-3">
              <div className="flex items-center gap-2 mb-1">
                <BarChart3 className="w-3.5 h-3.5 text-blue-400" />
                <p className="text-[10px] text-blue-300 uppercase font-bold">Kế toán — Doanh thu ngày</p>
              </div>
              <p className="text-base font-bold text-blue-300">{fmt(kt?.doanh_thu_total || 0)}đ</p>
              <p className="text-[10px] text-slate-500 mt-1">
                {kt?.entry_count || 0} dòng nhập | TM {fmt(kt?.tien_mat_total)}đ · CK {fmt(kt?.ck_total)}đ
              </p>
            </div>
            <div className="bg-gradient-to-br from-emerald-600/15 to-emerald-500/5 border border-emerald-500/30 rounded-xl p-3">
              <div className="flex items-center gap-2 mb-1">
                <BarChart3 className="w-3.5 h-3.5 text-emerald-400" />
                <p className="text-[10px] text-emerald-300 uppercase font-bold">Kế toán — Pippy (riêng)</p>
              </div>
              <p className="text-base font-bold text-emerald-300">{fmt(pippy?.total || 0)}đ</p>
              <p className="text-[10px] text-slate-500 mt-1">{pippy?.entry_count || 0} dòng menu hàng hoá</p>
            </div>
            <div className="bg-gradient-to-br from-orange-600/15 to-orange-500/5 border border-orange-500/30 rounded-xl p-3">
              <div className="flex items-center gap-2 mb-1">
                <BarChart3 className="w-3.5 h-3.5 text-orange-400" />
                <p className="text-[10px] text-orange-300 uppercase font-bold">KiotViet API — Hoá đơn</p>
              </div>
              <p className="text-base font-bold text-orange-300">{fmt(kv?.doanh_thu_total || 0)}đ</p>
              <p className="text-[10px] text-slate-500 mt-1">{kv?.invoice_count || 0} hoá đơn (status=completed)</p>
            </div>
          </div>

          {/* Big diff card */}
          <div
            className={`rounded-xl p-4 border ${
              Math.abs(diff?.doanh_thu || 0) === 0
                ? "bg-emerald-500/10 border-emerald-500/30"
                : Math.abs(diff?.doanh_thu || 0) > 100000
                ? "bg-red-500/10 border-red-500/30"
                : "bg-amber-500/10 border-amber-500/30"
            }`}
            data-testid="reconcile-big-diff"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-[10px] uppercase font-bold tracking-wide text-slate-400 mb-1">
                  Chênh lệch tổng (Kế toán Doanh thu ngày − KiotViet)
                </p>
                <p className={`text-2xl font-bold ${diffColor(diff?.doanh_thu)}`}>
                  {fmtDiff(diff?.doanh_thu || 0)}đ
                </p>
              </div>
              <div className="text-right">
                {Math.abs(diff?.doanh_thu || 0) === 0 ? (
                  <CheckCircle2 className="w-10 h-10 text-emerald-400" />
                ) : (
                  <AlertTriangle className={`w-10 h-10 ${Math.abs(diff?.doanh_thu) > 100000 ? "text-red-400" : "text-amber-400"}`} />
                )}
              </div>
            </div>
            <p className="text-[10.5px] text-slate-500 mt-2 italic">
              {Math.abs(diff?.doanh_thu || 0) === 0
                ? "Khớp tuyệt đối — không có chênh lệch."
                : (diff?.doanh_thu || 0) > 0
                ? "Sổ ghi nhận lớn hơn KiotViet → có thể có doanh thu chưa qua máy POS, hoặc nhập sai sổ."
                : "Sổ ghi nhận nhỏ hơn KiotViet → có thể thiếu ghi sổ, hoặc hoá đơn KiotViet bị huỷ chưa cập nhật."}
            </p>
          </div>

          {/* By tài khoản */}
          <div className="bg-[#1e293b] border border-[#334155] rounded-xl overflow-hidden" data-testid="reconcile-by-tai-khoan">
            <div className="px-4 py-2 border-b border-[#334155] bg-gradient-to-r from-indigo-500/10 to-purple-500/10">
              <h3 className="text-xs font-bold text-indigo-300 uppercase tracking-wide">Đối chiếu theo Tài khoản (Khu)</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="bg-[#0f172a] border-b border-[#334155]">
                    <th className="text-left p-2 text-slate-400 font-semibold">TÀI KHOẢN</th>
                    <th className="text-right p-2 text-blue-300 font-semibold">Kế toán</th>
                    <th className="text-right p-2 text-orange-300 font-semibold">KiotViet</th>
                    <th className="text-right p-2 text-amber-300 font-semibold">Chênh lệch</th>
                  </tr>
                </thead>
                <tbody>
                  {(diff?.by_tai_khoan_vs_branch || []).map((row) => (
                    <tr key={row.tai_khoan} className="border-b border-[#334155]/50 hover:bg-[#0f172a]/50" data-testid={`reconcile-tk-${row.tai_khoan.replace(/ /g, "-")}`}>
                      <td className="p-2 text-slate-100 font-medium">{row.tai_khoan}</td>
                      <td className="p-2 text-right text-blue-300">{fmt(row.ke_toan)}</td>
                      <td className="p-2 text-right text-orange-300">{fmt(row.kiotviet)}</td>
                      <td className={`p-2 text-right font-semibold ${diffColor(row.diff)}`}>{fmtDiff(row.diff)}</td>
                    </tr>
                  ))}
                  {(diff?.by_tai_khoan_vs_branch || []).length === 0 && (
                    <tr>
                      <td colSpan={4} className="p-4 text-center text-slate-500 text-xs italic">Không có dữ liệu trong khoảng thời gian này</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* By date */}
          <div className="bg-[#1e293b] border border-[#334155] rounded-xl overflow-hidden" data-testid="reconcile-by-date">
            <div className="px-4 py-2 border-b border-[#334155] bg-gradient-to-r from-teal-500/10 to-cyan-500/10">
              <h3 className="text-xs font-bold text-teal-300 uppercase tracking-wide flex items-center gap-2">
                <CalendarIcon className="w-3.5 h-3.5" /> Đối chiếu theo Ngày
              </h3>
            </div>
            <div className="overflow-x-auto max-h-[400px]">
              <table className="w-full text-xs">
                <thead className="sticky top-0">
                  <tr className="bg-[#0f172a] border-b border-[#334155]">
                    <th className="text-left p-2 text-slate-400 font-semibold">NGÀY</th>
                    <th className="text-right p-2 text-blue-300 font-semibold">Kế toán</th>
                    <th className="text-right p-2 text-orange-300 font-semibold">KiotViet</th>
                    <th className="text-right p-2 text-amber-300 font-semibold">Chênh lệch</th>
                  </tr>
                </thead>
                <tbody>
                  {(diff?.by_date || []).map((row) => (
                    <tr key={row.date} className="border-b border-[#334155]/50 hover:bg-[#0f172a]/50" data-testid={`reconcile-date-${row.date}`}>
                      <td className="p-2 text-slate-200">{formatDateVN(row.date)}</td>
                      <td className="p-2 text-right text-blue-300">{fmt(row.ke_toan)}</td>
                      <td className="p-2 text-right text-orange-300">{fmt(row.kiotviet)}</td>
                      <td className={`p-2 text-right font-semibold ${diffColor(row.diff)}`}>{fmtDiff(row.diff)}</td>
                    </tr>
                  ))}
                  {(diff?.by_date || []).length === 0 && (
                    <tr>
                      <td colSpan={4} className="p-4 text-center text-slate-500 text-xs italic">Không có dữ liệu</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* By KiotViet branch (raw) + payment method */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="bg-[#1e293b] border border-[#334155] rounded-xl overflow-hidden" data-testid="reconcile-by-branch-raw">
              <div className="px-4 py-2 border-b border-[#334155] bg-orange-500/10">
                <h3 className="text-xs font-bold text-orange-300 uppercase">KiotViet — theo Chi nhánh (raw)</h3>
              </div>
              <div className="p-2 text-[11px] space-y-1">
                {Object.entries(kv?.by_branch || {}).sort((a, b) => b[1] - a[1]).map(([bn, amt]) => (
                  <div key={bn} className="flex justify-between text-slate-300">
                    <span className="truncate">{bn || "(Không có chi nhánh)"}</span>
                    <span className="text-orange-300 font-mono">{fmt(amt)}đ</span>
                  </div>
                ))}
                {Object.keys(kv?.by_branch || {}).length === 0 && (
                  <p className="text-slate-500 italic text-center py-3">Không có dữ liệu KiotViet</p>
                )}
              </div>
            </div>
            <div className="bg-[#1e293b] border border-[#334155] rounded-xl overflow-hidden" data-testid="reconcile-by-payment">
              <div className="px-4 py-2 border-b border-[#334155] bg-violet-500/10">
                <h3 className="text-xs font-bold text-violet-300 uppercase">KiotViet — theo Phương thức TT</h3>
              </div>
              <div className="p-2 text-[11px] space-y-1">
                {Object.entries(kv?.by_payment_method || {}).sort((a, b) => b[1] - a[1]).map(([pm, amt]) => (
                  <div key={pm} className="flex justify-between text-slate-300">
                    <span className="truncate">{pm}</span>
                    <span className="text-violet-300 font-mono">{fmt(amt)}đ</span>
                  </div>
                ))}
                {Object.keys(kv?.by_payment_method || {}).length === 0 && (
                  <p className="text-slate-500 italic text-center py-3">Không có dữ liệu</p>
                )}
              </div>
            </div>
          </div>

          {/* Footer note */}
          <div className="text-[10px] text-slate-500 italic text-center pt-1 flex items-center justify-center gap-1.5">
            <Badge className="bg-slate-700/40 text-slate-300 border border-slate-600/30 text-[9px]">READ-ONLY</Badge>
            <span>Tab này chỉ tổng hợp số liệu từ 2 nguồn riêng biệt — không sửa, không ghi đè dữ liệu gốc.</span>
          </div>
        </>
      )}
    </div>
  );
}
